import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { applyAllTransformationsForEditing } from '../../utils/EmailTemplateTransformations';
import { APP_URL } from '../../config';
import { useTheme } from "@mui/material/styles";


const ReportedSimEmailPreviewModal = ({
    open,
    onClose,
    previewContent,
    tenancyData,
    universitySettings,
    colors
}) => {
    const [currentPreview, setCurrentPreview] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const theme = useTheme();

    // Sample data for reported sim email placeholders
    const sampleData = {
        firstName: "John",
        lastName: "Doe",
        tenancyName: tenancyData?.name || "Your Organization",
        simulationId: "SIM123456",
        originalSubject: "Urgent: Account Verification Required",
        fromAddress: "security@suspicious-bank.com",
        reportedAt: new Date().toLocaleString(),
        mainText: universitySettings?.reportedSimMessage || "You successfully reported a training simulation. Nice work!"
    };

    // Initialize when modal opens
    useEffect(() => {
        if (open && previewContent) {
            setIsInitialized(true);
        } else if (!open) {
            setIsInitialized(false);
            setCurrentPreview('');
        }
    }, [open, previewContent]);

    // Process the template content
    useEffect(() => {
        if (previewContent && open && isInitialized) {
            // Apply color and logo transformations
            const logoUrl = tenancyData?.logo ? `${APP_URL}/${tenancyData.logo}` : null;

            let processedTemplate = applyAllTransformationsForEditing(
                previewContent,
                colors || {
                    primary: '#0b5fff',
                    secondary: '#666666',
                    tertiary: '#cccccc'
                },
                logoUrl
            );

            // Replace all placeholders with sample data
            Object.entries(sampleData).forEach(([key, value]) => {
                const placeholder = `{${key}}`;
                processedTemplate = processedTemplate.replace(
                    new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                    value
                );
            });

            // Replace any remaining common placeholders
            processedTemplate = processedTemplate
                .replace(/\{user\.firstname\}/gi, sampleData.firstName)
                .replace(/\{user\.lastname\}/gi, sampleData.lastName)
                .replace(/\{client\.name\}/gi, sampleData.tenancyName)
                .replace(/\{tenancy\.name\}/gi, sampleData.tenancyName);

            // Wrap in HTML structure for proper display
            processedTemplate = `
                <html>
                <head>
                    <meta charset="utf-8">
                    <style>
                        body, html {
                            margin: 0;
                            padding: 20px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            background-color: #f5f5f5;
                        }
                        table {
                            border-collapse: collapse;
                            mso-table-lspace: 0pt;
                            mso-table-rspace: 0pt;
                        }
                        img {
                            border: 0;
                            height: auto;
                            line-height: 100%;
                            outline: none;
                            text-decoration: none;
                            max-width: 100%;
                        }
                    </style>
                </head>
                <body>
                    <div class="email-container">
                        ${processedTemplate}
                    </div>
                </body>
                </html>
            `;

            setCurrentPreview(processedTemplate);
        }
    }, [previewContent, open, isInitialized, tenancyData, universitySettings, colors, sampleData]);

    if (!isInitialized || !tenancyData || !universitySettings) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={40} />
                    <Typography sx={{ mt: 2 }}>Loading preview...</Typography>
                </Box>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '90vh',
                    backgroundColor: theme.palette.background.default
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="h4">Reported Sim Email Preview</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Display Name: {universitySettings?.reportedSimDisplayName || 'Boxphish'}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Subject: {universitySettings?.reportedSimEmailSubject || 'Simulation Report Confirmation'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Sample data is used for placeholders to show how the email will appear to users
                    </Typography>
                </Box>

                <iframe
                    srcDoc={currentPreview}
                    style={{
                        border: 'none',
                        width: '100%',
                        height: 'calc(90vh - 200px)',
                        backgroundColor: 'white',
                        borderRadius: '8px'
                    }}
                    title="Reported Sim Email Preview"
                    sandbox="allow-same-origin allow-scripts"
                    onLoad={(e) => {
                        // Prevent navigation in the iframe
                        const frame = e.target;
                        const frameDoc = frame.contentDocument || frame.contentWindow.document;

                        if (!frameDoc) return;

                        // Disable all links
                        const links = frameDoc.querySelectorAll('a');
                        links.forEach(link => {
                            link.onclick = (e) => {
                                e.preventDefault();
                                return false;
                            };
                            // Visual indicator that links are disabled
                            link.style.cursor = 'default';
                            link.title = 'Link disabled in preview';
                        });

                        // Disable all buttons and form submissions
                        const buttons = frameDoc.querySelectorAll('button, input[type="submit"], input[type="button"]');
                        buttons.forEach(button => {
                            button.onclick = (e) => {
                                e.preventDefault();
                                return false;
                            };
                            button.style.cursor = 'default';
                            button.title = 'Button disabled in preview';
                        });

                        // Disable all forms
                        const forms = frameDoc.querySelectorAll('form');
                        forms.forEach(form => {
                            form.onsubmit = (e) => {
                                e.preventDefault();
                                return false;
                            };
                        });
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default ReportedSimEmailPreviewModal;